import React from 'react';
import { ProjectStatus, AssetStatus } from '../types';

interface Props {
  projectStatus: ProjectStatus | null;
  isConnected: boolean;
  onBackToHome?: () => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return '⏳';
    case 'processing':
      return '🔄';
    case 'completed':
      return '✅';
    case 'failed':
      return '❌';
    default:
      return '❓';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'text-yellow-600 bg-yellow-100';
    case 'processing':
      return 'text-blue-600 bg-blue-100';
    case 'completed':
      return 'text-green-600 bg-green-100';
    case 'failed':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

const ProgressDisplay: React.FC<Props> = ({ projectStatus, isConnected, onBackToHome }) => {
  if (!projectStatus) {
    return null;
  }

  const { progressPercentage, completedAssets, totalAssets, assets, status } = projectStatus;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      {/* 头部信息 */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">
          📊 生成进度
        </h2>
        <div className="flex items-center space-x-4">
          {/* 回到首页按钮 */}
          {onBackToHome && (
            <button
              onClick={onBackToHome}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              🏠 回到首页
            </button>
          )}
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-600">
              {isConnected ? '实时连接' : '连接断开'}
            </span>
          </div>
        </div>
      </div>

      {/* 总体进度 */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            总体进度: {completedAssets}/{totalAssets}
          </span>
          <span className="text-sm font-medium text-gray-700">
            {progressPercentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* 项目状态 */}
      <div className="mb-6">
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
          {getStatusIcon(status)}
          <span className="ml-2">
            {status === 'planning' && '规划中'}
            {status === 'generating' && '生成中'}
            {status === 'completed' && '已完成'}
            {status === 'failed' && '失败'}
          </span>
        </div>
      </div>

      {/* 素材列表 */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">素材详情</h3>
        {assets.map((asset: AssetStatus) => (
          <div key={asset.assetId} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <span className="text-lg mr-2">{getStatusIcon(asset.status)}</span>
                <div>
                  <h4 className="font-medium text-gray-800">{asset.assetName}</h4>
                  <p className="text-sm text-gray-600">
                    {asset.assetType === 'character' && '角色'}
                    {asset.assetType === 'background' && '背景'}
                    {asset.assetType === 'item' && '道具'}
                    {asset.assetType === 'ui' && '界面'}
                  </p>
                </div>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                {asset.status === 'pending' && '等待中'}
                {asset.status === 'processing' && '处理中'}
                {asset.status === 'completed' && '已完成'}
                {asset.status === 'failed' && '失败'}
              </div>
            </div>

            {/* 进度条 */}
            {asset.status === 'processing' && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${asset.progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">处理进度: {asset.progress}%</p>
              </div>
            )}

            {/* 完成时间 */}
            {asset.completedAt && (
              <p className="text-xs text-gray-500 mt-2">
                完成时间: {new Date(asset.completedAt).toLocaleString()}
              </p>
            )}

            {/* 图片预览 */}
            {asset.imageUrl && (
              <div className="mt-3">
                <img
                  src={asset.imageUrl}
                  alt={asset.assetName}
                  className="w-24 h-24 object-cover rounded-lg border"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 预计完成时间 */}
      {projectStatus.estimatedCompletionTime && status !== 'completed' && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-medium">🕒 预计完成时间:</span> {projectStatus.estimatedCompletionTime}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgressDisplay;